import { useEffect, useState } from 'react';
import { Modal } from 'src/ui/Modal';
import { Button } from 'src/ui/Button';
import { Field } from 'src/ui/Field';
import { Input } from 'src/ui/Input';
import { Textarea } from 'src/ui/Textarea';
import { Badge } from 'src/ui/Badge';
import { useToast } from 'src/ui/Toast';
import {
  EventNotificationCopy,
  FEEDBACK_TEMPLATE_KEY,
  useFetchEventNotificationTemplateQuery,
  useResetEventNotificationTemplateMutation,
  useSaveEventNotificationTemplateMutation,
} from 'src/store/eventApi';

interface FeedbackMessageModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const BLANK: EventNotificationCopy = {
  titleEn: '',
  bodyEn: '',
  titleCn: '',
  bodyCn: '',
};

/**
 * Edits the copy of the post-event feedback push. The server ships a default
 * and stores an edit as an override, so "Reset" deletes the override rather
 * than writing the default back — a later change to the shipped copy then
 * still reaches anyone who never customized it.
 */
const FeedbackMessageModal = ({
  isOpen,
  onClose,
}: FeedbackMessageModalProps) => {
  const toast = useToast();
  const { data, isFetching } = useFetchEventNotificationTemplateQuery(
    FEEDBACK_TEMPLATE_KEY,
    { skip: !isOpen }
  );
  const [saveTemplate, saveStatus] = useSaveEventNotificationTemplateMutation();
  const [resetTemplate, resetStatus] =
    useResetEventNotificationTemplateMutation();
  const [form, setForm] = useState<EventNotificationCopy>(BLANK);

  useEffect(() => {
    if (data) setForm(data.current);
  }, [data]);

  const set = <K extends keyof EventNotificationCopy>(
    key: K,
    value: EventNotificationCopy[K]
  ) => setForm((f) => ({ ...f, [key]: value }));

  const onSave = async () => {
    const blank = (Object.keys(form) as (keyof EventNotificationCopy)[]).find(
      (k) => !form[k].trim()
    );
    if (blank) {
      // Both languages or neither: the server picks per recipient, so a
      // half-filled override would split the guest list across two versions.
      toast({ type: 'error', title: 'Every field is required' });
      return;
    }
    try {
      await saveTemplate({ key: FEEDBACK_TEMPLATE_KEY, copy: form }).unwrap();
      toast({ type: 'success', title: 'Message saved' });
      onClose();
    } catch (e: any) {
      toast({
        type: 'error',
        title: 'Save failed',
        message: e?.data?.message || e?.message,
      });
    }
  };

  const onReset = async () => {
    try {
      const next = await resetTemplate(FEEDBACK_TEMPLATE_KEY).unwrap();
      setForm(next.current);
      toast({ type: 'info', title: 'Back to the default message' });
    } catch (e: any) {
      toast({
        type: 'error',
        title: 'Reset failed',
        message: e?.data?.message || e?.message,
      });
    }
  };

  const busy = saveStatus.isLoading || resetStatus.isLoading || isFetching;

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title="Feedback reminder message"
      subtitle="Sent to every seated guest after an event. {title} becomes the event's name."
      width={620}
      footer={
        <div className="row gap-2" style={{ justifyContent: 'space-between' }}>
          <Button
            variant="ghost"
            size="sm"
            onClick={onReset}
            disabled={busy || !data?.isOverridden}
          >
            Reset to default
          </Button>
          <div className="row gap-2">
            <Button variant="secondary" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={onSave}
              loading={saveStatus.isLoading}
              disabled={busy}
            >
              Save
            </Button>
          </div>
        </div>
      }
    >
      <div className="col gap-3">
        <div className="row gap-2" style={{ alignItems: 'center' }}>
          <Badge color={data?.isOverridden ? 'amber' : 'gray'} size="sm">
            {data?.isOverridden ? 'Customized' : 'Default'}
          </Badge>
          {data?.updatedAt && (
            <span className="text-muted" style={{ fontSize: 'var(--fs-xs)' }}>
              Edited {new Date(data.updatedAt).toLocaleString()}
            </span>
          )}
        </div>

        <Field label="Title (中文)">
          <Input
            value={form.titleCn}
            onChange={(e) => set('titleCn', e.target.value)}
            placeholder={data?.defaults.titleCn}
          />
        </Field>
        <Field label="Body (中文)">
          <Textarea
            rows={3}
            value={form.bodyCn}
            onChange={(e) => set('bodyCn', e.target.value)}
            placeholder={data?.defaults.bodyCn}
          />
        </Field>
        <Field label="Title (English)">
          <Input
            value={form.titleEn}
            onChange={(e) => set('titleEn', e.target.value)}
            placeholder={data?.defaults.titleEn}
          />
        </Field>
        <Field
          label="Body (English)"
          hint="Guests see the language they picked, so both versions have to say the same thing."
        >
          <Textarea
            rows={3}
            value={form.bodyEn}
            onChange={(e) => set('bodyEn', e.target.value)}
            placeholder={data?.defaults.bodyEn}
          />
        </Field>
      </div>
    </Modal>
  );
};

export default FeedbackMessageModal;
